import { VtkRenderWindowParentContext } from '@/src/components/vtk/context';
import { onVTKEvent } from '@/src/composables/onVTKEvent';
import { registerViewForScreenshot } from '@/src/core/screenshot';
import { View } from '@/src/core/vtk/types';
import { Maybe } from '@/src/types';
import { VtkRenderWindowParentApi } from '@/src/types/vtk-types';
import { batchForNextTask } from '@/src/utils/batchForNextTask';
import vtkRenderWindow from '@kitware/vtk.js/Rendering/Core/RenderWindow';
import vtkRenderWindowInteractor from '@kitware/vtk.js/Rendering/Core/RenderWindowInteractor';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
import vtkOpenGLRenderWindow from '@kitware/vtk.js/Rendering/OpenGL/RenderWindow';
import vtkWidgetManager from '@kitware/vtk.js/Widgets/Core/WidgetManager';
import { useElementSize } from '@vueuse/core';
import {
  inject,
  MaybeRef,
  onScopeDispose,
  unref,
  watchEffect,
  watchPostEffect,
} from 'vue';

// vtk.js' public interactor.cancelAnimation() is a no-op while the
// post-wheel extension window is still open (model._animationExtendedEnd),
// so on dispose during that window a pending rAF survives and fires against
// the deleted interactor. Reach into the private animationRequest field to
// cancel it directly.
function cancelPendingInteractorAnimationFrame(
  interactor: vtkRenderWindowInteractor
) {
  const model = (
    interactor as unknown as { get(): { animationRequest?: number | null } }
  ).get();
  if (model.animationRequest == null) return;

  cancelAnimationFrame(model.animationRequest);
  model.animationRequest = null;
}

export function useWebGLRenderWindow(
  renderWindow: vtkRenderWindow,
  container: MaybeRef<Maybe<HTMLElement>>,
  parent: Maybe<VtkRenderWindowParentApi>
) {
  const renderWindowView =
    (parent?.renderWindowView.addMissingNode(renderWindow) as
      | vtkOpenGLRenderWindow
      | undefined) ?? vtkOpenGLRenderWindow.newInstance();

  watchPostEffect((onCleanup) => {
    const el = unref(container);
    if (!el) return;

    renderWindowView.setContainer(el);
    onCleanup(() => {
      renderWindowView.setContainer(null as unknown as HTMLElement);
    });
  });

  onScopeDispose(() => {
    if (parent?.renderWindowView) {
      parent.renderWindowView.removeNode(renderWindowView);
    } else {
      renderWindowView.delete();
    }
  });

  renderWindow.addView(renderWindowView);

  onScopeDispose(() => {
    renderWindow.removeView(renderWindowView);
  });

  return renderWindowView;
}

export function useWidgetManager(renderer: vtkRenderer) {
  const manager = vtkWidgetManager.newInstance();
  manager.setRenderer(renderer);

  const updatePickingState = () => {
    const enabled = manager.getPickingEnabled();
    const widgetCount = manager.getWidgets().length;
    if (!enabled && widgetCount) {
      manager.enablePicking();
    } else if (enabled && !widgetCount) {
      manager.disablePicking();
    }
  };

  onVTKEvent(manager, 'onModified', updatePickingState);
  updatePickingState();

  return manager;
}

export function useVtkView(container: MaybeRef<Maybe<HTMLElement>>): View {
  const parent = inject(VtkRenderWindowParentContext, null);
  const renderer = vtkRenderer.newInstance();
  const renderWindow = vtkRenderWindow.newInstance();
  renderWindow.addRenderer(renderer);

  parent?.renderWindow.addRenderWindow(renderWindow);

  // the render window view
  const renderWindowView = useWebGLRenderWindow(
    renderWindow,
    container,
    parent
  );

  // interactor
  const interactor = vtkRenderWindowInteractor.newInstance();
  renderWindow.setInteractor(interactor);
  interactor.setView(renderWindowView);

  watchPostEffect((onCleanup) => {
    const el = unref(container);
    if (!el) return;

    interactor.initialize();
    interactor.setContainer(el);
    onCleanup(() => {
      if (interactor.getContainer()) interactor.unbindEvents();
    });
  });

  // widget manager
  const widgetManager = useWidgetManager(renderer);

  // render API
  const deferredRender = batchForNextTask(() => {
    // don't need to re-render during animation
    if (interactor.isAnimating()) return;
    widgetManager.renderWidgets();
    renderWindow.render();
  });

  const immediateRender = () => {
    if (interactor.isAnimating()) return;
    parent?.renderWindow.render();
    renderWindow.render();
  };

  const requestRender = ({ immediate } = { immediate: false }) => {
    if (immediate) {
      immediateRender();
    }
    deferredRender();
  };

  onVTKEvent(renderer, 'onModified', () => {
    requestRender();
  });

  // set size
  const setSize = (width: number, height: number) => {
    // ensure we have a non-zero size, otherwise
    // the framebuffers might not be populated correctly
    const scaledWidth = Math.max(1, width * globalThis.devicePixelRatio);
    const scaledHeight = Math.max(1, height * globalThis.devicePixelRatio);
    renderWindowView.setSize(scaledWidth, scaledHeight);
    renderer.resetCameraClippingRange();

    parent?.renderWindowView.resizeFromChildRenderWindows();
    parent?.renderWindow.render();
    requestRender({ immediate: true });
  };

  const { width, height } = useElementSize(container);
  watchEffect(() => {
    setSize(width.value, height.value);
  });

  const viewApi: View = {
    renderer,
    renderWindow,
    interactor,
    renderWindowView,
    widgetManager,
    requestRender,
  };

  // register for the toolbar screenshot action
  const unregisterScreenshot = registerViewForScreenshot(viewApi, container);

  // cleanup
  onScopeDispose(() => {
    unregisterScreenshot();
    deferredRender.cancel();

    renderWindow.removeRenderer(renderer);
    parent?.renderWindow.removeRenderWindow(renderWindow);

    renderer.delete();
    renderWindow.delete();
    cancelPendingInteractorAnimationFrame(interactor);
    interactor.delete();
  });

  return viewApi;
}
